import type { ReactElement } from 'react';
import { useState } from 'react';
import type { AdminSpecialization } from '@cookspace-shared-contracts/core';
import { Plus } from 'lucide-react';

import {
    useCreateAdminProfessionalSpecializationMutation,
    useDeleteAdminProfessionalSpecializationMutation,
    useListAdminProfessionalSpecializationsQuery,
    useReorderAdminProfessionalSpecializationsMutation,
    useUpdateAdminProfessionalSpecializationMutation
} from '@/api/professionalSpecializationsApi';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { SpecializationFormResult } from '@/components/SpecializationEditModal';
import { SpecializationEditModal } from '@/components/SpecializationEditModal';
import type { ReorderPayload } from '@/components/SpecializationTree';
import { SpecializationTree } from '@/components/SpecializationTree';
import type { SpecializationTreeNode } from '@/lib/buildSpecializationTree';

export function SpecializationsPage(): ReactElement {
    const { data, isLoading, isError } = useListAdminProfessionalSpecializationsQuery();

    const [createSpec, { isLoading: isCreating }] =
        useCreateAdminProfessionalSpecializationMutation();
    const [updateSpec, { isLoading: isUpdating }] =
        useUpdateAdminProfessionalSpecializationMutation();
    const [deleteSpec] = useDeleteAdminProfessionalSpecializationMutation();
    const [reorderSpec] = useReorderAdminProfessionalSpecializationsMutation();

    const [isEditOpen, setEditOpen] = useState(false);
    const [editing, setEditing] = useState<AdminSpecialization | null>(null);
    const [creatingUnderParent, setCreatingUnderParent] = useState<AdminSpecialization | null>(
        null
    );
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [pageError, setPageError] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<AdminSpecialization | null>(null);

    const items = data?.items ?? [];

    const openCreateRoot = (): void => {
        setEditing(null);
        setCreatingUnderParent(null);
        setSubmitError(null);
        setEditOpen(true);
    };

    const openCreateChild = (parent: SpecializationTreeNode): void => {
        setEditing(null);
        setCreatingUnderParent(parent);
        setSubmitError(null);
        setEditOpen(true);
    };

    const openEdit = (node: SpecializationTreeNode): void => {
        setEditing(node);
        setCreatingUnderParent(null);
        setSubmitError(null);
        setEditOpen(true);
    };

    const closeEdit = (): void => {
        setEditOpen(false);
        setEditing(null);
        setCreatingUnderParent(null);
        setSubmitError(null);
    };

    const submitForm = async (values: SpecializationFormResult): Promise<void> => {
        setSubmitError(null);
        try {
            if (editing !== null) {
                await updateSpec({
                    id: editing.id,
                    name: values.name,
                    slug: values.slug,
                    sortOrder: values.sortOrder,
                    isActive: values.isActive,
                    canPublishRecipes:
                        editing.parentId === null ? values.canPublishRecipes : null
                }).unwrap();
            } else {
                const body: {
                    name: string;
                    slug: string;
                    sortOrder: number;
                    parentId?: string | null;
                    canPublishRecipes?: boolean | null;
                } = {
                    name: values.name,
                    slug: values.slug,
                    sortOrder: values.sortOrder
                };
                if (creatingUnderParent !== null) {
                    body.parentId = creatingUnderParent.id;
                } else {
                    body.parentId = null;
                    body.canPublishRecipes = values.canPublishRecipes;
                }
                await createSpec(body).unwrap();
            }
            closeEdit();
        } catch {
            setSubmitError('Не удалось сохранить узел');
        }
    };

    const confirmDelete = async (): Promise<void> => {
        if (!pendingDelete) {
            return;
        }
        try {
            await deleteSpec({ id: pendingDelete.id }).unwrap();
            setPendingDelete(null);
        } catch {
            setPendingDelete(null);
            setPageError('Не удалось скрыть узел');
        }
    };

    const handleReorder = async (payload: ReorderPayload): Promise<void> => {
        try {
            await reorderSpec(payload).unwrap();
        } catch {
            setPageError('Не удалось изменить порядок');
        }
    };

    const handleMove = async (
        nodeId: string,
        newParentId: string | null
    ): Promise<void> => {
        try {
            await updateSpec({ id: nodeId, parentId: newParentId }).unwrap();
        } catch {
            setPageError('Не удалось переместить узел');
        }
    };

    return (
        <div data-testid="specializations-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Специализации</h1>
                <button
                    type="button"
                    onClick={openCreateRoot}
                    data-testid="specialization-create-root-button"
                    className="inline-flex items-center gap-2 rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    <Plus size={14} />
                    Создать корневой узел
                </button>
            </div>

            <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                Дерево специализаций определяет варианты, которые профи может выбрать в
                заявке. Перетащите узел на другой — он станет дочерним. Перетащите внутри
                одной группы — измените порядок. Корневые узлы могут задавать право
                публикации контента для всех потомков.
            </div>

            {pageError !== null && pageError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="specializations-page-error"
                >
                    <span>{pageError}</span>
                    <button
                        type="button"
                        onClick={() => setPageError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="specializations-error">
                    Не удалось загрузить специализации
                </div>
            )}

            {!isLoading && !isError && (
                <div className="rounded border border-gray-200 bg-white p-2">
                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Специализации не созданы. Нажмите «Создать корневой узел».
                        </div>
                    )}
                    {items.length > 0 && (
                        <SpecializationTree
                            items={items}
                            onEdit={openEdit}
                            onDelete={(node) => setPendingDelete(node)}
                            onAddChild={openCreateChild}
                            onReorder={(payload) => {
                                void handleReorder(payload);
                            }}
                            onMove={(nodeId, parentId) => {
                                void handleMove(nodeId, parentId);
                            }}
                            draggable
                        />
                    )}
                </div>
            )}

            <SpecializationEditModal
                isOpen={isEditOpen}
                editing={editing}
                parent={creatingUnderParent}
                isSaving={isCreating || isUpdating}
                submitError={submitError}
                onClose={closeEdit}
                onSubmit={(values) => {
                    void submitForm(values);
                }}
            />

            <ConfirmDialog
                isOpen={pendingDelete !== null}
                title="Скрыть узел"
                message={
                    pendingDelete
                        ? `Скрыть узел «${pendingDelete.name}»? Это не удалит связи у профи, но узел перестанет отображаться при выборе.`
                        : ''
                }
                confirmLabel="Скрыть"
                danger
                onConfirm={() => {
                    void confirmDelete();
                }}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
}
